// Import Dependencies
import { Navigate } from 'react-router';

// Local Imports
import { AppLayout } from 'app/layouts/AppLayout';
import { DynamicLayout } from 'app/layouts/DynamicLayout';
import AuthGuard from 'middleware/AuthGuard';
import OnboardingPage from 'app/pages/shared/onBoarding/onBoardingPage';

// ----------------------------------------------------------------------

const protectedRoutes = {
  id: 'protected',
  Component: AuthGuard,
  children: [
    // The dynamic layout supports both the main layout and the sideblock.
    {
      // id: 'public',
      children: [
        {
          index: true, // <-- page par défaut
          element: <OnboardingPage />,
        },
        {
          path: 'onboarding',
          element: <OnboardingPage />,
        },
      ],
    },
    {
      Component: DynamicLayout,
      children: [
        {
          path: 'dashboard',
          lazy: async () => ({
            Component: (await import('app/pages/manager/dashboardPage'))
              .default,
          }),
        },
        {
          path: 'reports',

          children: [
            {
              index: true,
              element: <Navigate to="/reports/daily-profile" />,
            },
            {
              path: 'daily-profile',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/DailyProfile/DailyProfiles'
                  )
                ).default,
              }),
            },
            {
              path: 'weather',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/WeatherAnalysis/WeatherAnalysis'
                  )
                ).default,
              }),
            },
            {
              path: 'annual',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/reports/AnnualQI/AnnualAqi')
                ).default,
              }),
            },
            {
              path: 'seasonal-analysis',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/SeasonalAnalysis/SeasonalAnalysisPage'
                  )
                ).default,
              }),
            },

            {
              path: 'periodic-analysis',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/PeriodicAnalysis/PeriodicAnalysis'
                  )
                ).default,
              }),
            },

            {
              path: 'correlation-analysis',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/CorrelationAnalysis/CorrelationAnalysisPage'
                  )
                ).default,
              }),
            },
          ],
        },
        {
          path: 'air-pollution',
          lazy: async () => ({
            Component: (await import('app/pages/manager/airPollutionPage'))
              .default,
          }),
        },
        {
          path: 'climate',
          lazy: async () => ({
            Component: (await import('app/pages/manager/climatePage')).default,
          }),
        },
        {
          path: 'urbanism',
          lazy: async () => ({
            Component: (await import('app/pages/manager/urbanismPage')).default,
          }),
        },
        {
          path: 'monitoring',
          children: [
            {
              index: true,
              element: <Navigate to="/monitoring/analytics" />,
            },
            {
              path: 'analytics',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/reports/DataAnalytics')
                ).default,
              }),
            },
            {
              path: 'daily-profile',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/DailyProfile/DailyProfiles'
                  )
                ).default,
              }),
            },
            {
              path: 'weather',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/WeatherAnalysis/WeatherAnalysis'
                  )
                ).default,
              }),
            },
            {
              path: 'annual',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/reports/AnnualQI/AnnualAqi')
                ).default,
              }),
            },
          ],
        },
        {
          path: 'meteorology',
          lazy: async () => ({
            Component: (await import('app/pages/manager/meteorologyPage'))
              .default,
          }),
        },
        {
          path: 'health',
          lazy: async () => ({
            Component: (await import('app/pages/manager/healthPage')).default,
          }),
        },
        {
          path: 'daily-forecast',
          lazy: async () => ({
            Component: (await import('app/pages/manager/dailyForecastPage'))
              .default,
          }),
        },
        {
          path: 'station/:stationId/calibrations/:calibrationId',
          lazy: async () => ({
            Component: (
              await import('app/pages/shared/devices/CalibrationDetails')
            ).default,
          }),
        },
        {
          path: 'devices/calibration/add',
          lazy: async () => ({
            Component: (await import('app/pages/shared/devices/Calibration'))
              .default,
          }),
        },
        {
          path: 'device-details/:stationId',
          lazy: async () => ({
            Component: (
              await import('app/pages/shared/devices/DeviceManagementDetails')
            ).default,
          }),
        },
        {
          path: 'alert-details/:alertId',
          lazy: async () => ({
            Component: (await import('app/pages/shared/alerts/AlertDetail'))
              .default,
          }),
        },
        {
          path: 'threshold-details/:thresholdId',
          lazy: async () => ({
            Component: (await import('app/pages/shared/alerts/ThresholdDetail'))
              .default,
          }),
        },
        {
          path: 'help',
          lazy: async () => ({
            Component: (await import('app/pages/monitoring/helpPage')).default,
          }),
        },
      ],
    },
    // The app layout supports only the main layout. Avoid using it for other layouts.
    {
      Component: AppLayout,
      children: [
        {
          path: 'settings',
          lazy: async () => ({
            Component: (await import('app/pages/shared/settings/Layout'))
              .default,
          }),
          children: [
            {
              index: true,
              element: <Navigate to="/settings/general" />,
            },
            {
              path: 'general',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/settings/sections/General')
                ).default,
              }),
            },
            {
              path: 'appearance',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/settings/sections/Appearance')
                ).default,
              }),
            },
          ],
        },
      ],
    },
  ],
};

export { protectedRoutes };
